/**
 * 账本快照（**N 域 N4 · `AC-N4.21`「覆盖前快照 用户可见、可回滚」**）—— 契约与**唯一口径常量**
 *
 * 依据：N 域 PRD **§7.4 `AC-N4.21`**（验收 = UI 走查 · **必测**）· **`DR-039`**
 * （覆盖前快照载体与回滚口径 · **已拍板生效 v1.1 · 2026-10-12**）。
 *
 * ## 🔴 快照 = 「把某一刻的导出产物留在库里」（`DR-039` §2.4 的边界）
 *
 * 本文件**不定义任何备份格式** —— `payload` 是 `contract/backup.ts` 的 `toBackupJson` 产物，
 * **逐字原文**。由此得到三个"不要钱"的性质：
 *
 * | 性质 | 为什么自动成立 |
 * |---|---|
 * | 快照能被**原样下载**给用户 | `payload` 就是导出文件的内容（同字节） |
 * | 快照能被**原样喂回**恢复接口 | 它 `format === BACKUP_FORMAT`（恢复侧认识的形状） |
 * | 回滚**继承恢复侧全部闸门** | 回滚 = 调 `overwrite`（先父后子 / 缺父表拒绝 / 引用完整性核验） |
 *
 * ⇒ 🔴 **端侧不得为快照另写序列化/校验**（铁律 24）。端侧只做两件事：展示、把 `payload` 送回。
 *
 * ## 🔴 为什么「原因」用英文 key + 中文标签分开（`DR-039` §2.2）
 *
 * `reason` 是**库里的稳定标识**（会进备份、会跨版本存活）⇒ 必须是英文闭集；
 * 而 UI 要显示中文 ⇒ 标签在本文件**按 key 直出**。
 * 这样做的收益：**新增一种原因时，`satisfies` 会让编译器指出"标签漏了"**，
 * 而不是等到界面上出现一个空白角标。
 *
 * ## ⚠️ 保留 5 份是**服务端**的策略，端侧只展示
 *
 * 端侧**不判断**"该删哪一份"（那是写入事务里的事，见 `restore.service.ts`）。
 * 本文件只把 `SNAPSHOT_KEEP_LIMIT` 下发，供 UI 写"最多保留 N 份"这类**说明性**文案。
 */

import { z } from 'zod';

/**
 * 快照原因闭集。
 *
 * | key | 何时落 | 为什么需要它 |
 * |---|---|---|
 * | `beforeOverwrite` | 执行 `overwrite` 恢复**之前**（`AC-N4.21` 的主体） | 覆盖会清空本账本内容 ⇒ 这是唯一的"后悔药" |
 * | `beforeRollback` | 执行**回滚**（= 一次 `overwrite`）之前 | 🔴 **防递归死局**：回滚本身也是破坏性动作，若不落快照，用户回滚到"错的"那份之后就**再也回不来**了（`DR-039` §4.3） |
 *
 * ⚠️ **`merge` 模式不落快照** —— 它是**累加**（只插本地没有的主键），可逆性远高于覆盖
 * （且 PRD 的 `AC-N4.21` 原文限定在"覆盖前"）⇒ 落快照反而会让 5 份额度被非破坏性操作挤满。
 */
export const SNAPSHOT_REASONS = ['beforeOverwrite', 'beforeRollback'] as const;

/** 一种快照原因（英文 key —— 进库、进备份、跨版本存活） */
export type SnapshotReason = (typeof SNAPSHOT_REASONS)[number];

/**
 * 原因 → 中文标签（**唯一来源** · 端侧直出，勿再包一层文案）。
 *
 * 🔴 `satisfies` 让"漏了一种原因"变成**编译错**（而不是界面上一个空角标）。
 */
const REASON_LABELS = {
  beforeOverwrite: '覆盖前自动备份',
  beforeRollback: '回滚前自动备份',
} satisfies Record<SnapshotReason, string>;

/**
 * 取原因的中文标签。
 *
 * ⚠️ 兜底分支**刻意返回原 key**（不是空串、也不是"未知原因"）：真出现库里有新原因
 * 而端侧还不认识（跨版本窗口）时，显示 `beforeSomething` 比显示空白更利于排障 ——
 * 用户能把它念给客服，空白只能让人猜。
 */
export function snapshotReasonLabel(reason: string): string {
  return (REASON_LABELS as Record<string, string | undefined>)[reason] ?? reason;
}

/**
 * 🔴 **每个账本保留的快照份数上限**（`DR-039` §3.2 裁定 = **5**）。
 *
 * 判据：5 份足以覆盖"连续几次误操作"的真实场景，同时让体积有可预测的上限
 * （实测 620KB 库的 JSON 约 1.5MB ⇒ 5 份 ≈ 7.5MB）。
 *
 * ⚠️ **裁掉最旧的**发生在写入的**同一个事务里**（不是定时清理）——
 * 否则会出现"新快照没落上、旧快照已被裁掉"的空窗。
 */
export const SNAPSHOT_KEEP_LIMIT = 5;

/**
 * 列表里的一条快照（**不含 `payload`** —— 那是 MB 级内容，列表不带）。
 *
 * 🔴 `payload` 单独走下载接口（`GET /api/settings/snapshots/:id/download`）——
 * 把它塞进列表 = 每次打开设置页都传 N × 1.5MB。
 */
export interface SnapshotSummaryView {
  id: string;
  /** 快照时刻（UTC 毫秒）= 该次导出的 `exportedAt`（与下载到的文件里逐字相同） */
  createdAt: number;
  /** 触发的**英文 key**；中文文案走 {@link snapshotReasonLabel}（端侧直出） */
  reason: string;
  /** 快照时刻的 schema 版本（排障线索 —— 不是回滚判据） */
  schemaVersion: number;
  /** `payload` 字节数（⚠️ 冗余列：写入时算一次，列表就不必读整份 payload） */
  sizeBytes: number;
  /** 触发者 id（`null` = 历史数据/无法归属）；姓名由端侧按成员名单解析，**服务端不下发文案** */
  createdById: string | null;
}

/**
 * 快照列表视图。
 *
 * ⚠️ `keepLimit` **下发而不是让端侧写死 5**：它同时用于"最多保留 N 份"的说明文案。
 * 端侧写死 ⇒ 服务端将来调整额度时，界面会**静默说谎**。
 */
export interface SnapshotListView {
  items: SnapshotSummaryView[];
  /** 当前保留上限（= {@link SNAPSHOT_KEEP_LIMIT}）—— 供 UI 写说明文案 */
  keepLimit: number;
}

/**
 * 回滚请求体。
 *
 * 🔴 **只有 `snapshotId`，没有别的字段** —— 回滚的目标由快照自身决定（`payload` 里的账本 id），
 * 端侧传不了"回滚到哪本账"这种东西。服务端仍会走恢复侧的**同账本校验**（跨账本 ⇒ 400）。
 *
 * ⚠️ 用户确认（"我知道这会覆盖当前数据"）**不在契约里** —— 它是纯 UI 层的二次确认
 * （回滚是 owner 权限，且快照本身就带着"回滚前再落一份"的保护 ⇒ 不需要再多一道旗标）。
 * 这与 `overwrite` 恢复**刻意不同**：那里要 `exportedBeforehand` + 二次输入账本名，
 * 因为它是**从外部文件**导入（用户可能拿着别人的文件）；这里的数据**本来就出自本账本**。
 */
export interface RollbackSnapshotRequest {
  snapshotId: string;
}

/**
 * 回滚请求的 Zod 契约（服务端经 `nestjs-zod` 管道校验 —— 端侧**不写第二份**非空判断）。
 *
 * ⚠️ 只校验"是个非空字符串"：id 的**存在性与归属**由服务端查库判定（404 / 400），
 * 在契约里写格式正则 = 把 id 生成规则抄第二遍（那条规则在 `platform/id` 里）。
 */
export const RollbackSnapshotRequestSchema = z.object({
  snapshotId: z.string().min(1).max(64),
});

// ─────────────────────── 端侧纯逻辑（**可测**，与 React 无关） ───────────────────────

/**
 * 人类可读的大小（`1536000` → `"1.5 MB"`）。
 *
 * 🔴 **这是"给人看的量级"，不是金额** ⇒ 不能走 `formatMoneyText`（那条线是**金额格式化的唯一实现**，
 * 用于钱；体积与钱无关）。⚠️ 本仓 ESLint 禁 `/` 与 `Math.round`（防金额运算绕开定点白名单），
 * 故此处显式豁免并写明理由。
 *
 * 为什么不用 `Intl.NumberFormat`：它在不同运行时的输出可能带不换行空格（`\u00a0`），
 * 那会让"对字"的断言变成环境相关的假红。手写 4 行换来**字节级可断言**。
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  // eslint-disable-next-line no-restricted-syntax -- 体积换算，与金额定点运算无关
  const kb = bytes / 1024;
  if (kb < 1024) {
    // eslint-disable-next-line no-restricted-syntax -- 同上
    return `${kb.toFixed(1)} KB`;
  }
  // eslint-disable-next-line no-restricted-syntax -- 同上
  const mb = kb / 1024;
  // eslint-disable-next-line no-restricted-syntax -- 同上
  return `${mb.toFixed(1)} MB`;
}

/**
 * 列表按时间**倒序**（最新在前）。
 *
 * 🔴 排序放在**共享层纯函数**里而不是服务端 SQL 里"顺手排了"：端侧拿到列表后可能还要
 * 与本地新增的项合并（乐观更新），两条路径的排序规则必须**同一份**，
 * 否则会出现"刷新后顺序跳一下"。
 *
 * 返回值是**新数组**（不改入参 —— 那个数组可能来自 React Query 的缓存）。
 */
export function sortSnapshots(items: readonly SnapshotSummaryView[]): SnapshotSummaryView[] {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
}
